import { type SelectOption } from '@factorialco/deprecated-design-system'
import { Field } from 'factorial-form'

import { useMemo } from 'modules/core/hooks/useMemo'
import i18n from 'modules/core/lib/i18n'

import locationsCollection from 'modules/locations/store/collections/locations'
import workAreasCollection from 'modules/locations/store/collections/workAreas'
import type WorkArea from 'modules/locations/store/models/WorkArea'
import { SYMBOLIC_MIXED_VALUE } from 'modules/shift_management/lib/types'

const SORT_PRIORITY_PREFIX = ' '

interface Props {
  locationField: Field
  mixedValuesLocation?: boolean
  mixedValuesWorkArea?: boolean
  hideNoLocationOption?: boolean
}

const useLocationOptions = ({
  locationField,
  mixedValuesLocation,
  mixedValuesWorkArea,
  hideNoLocationOption = false,
}: Props) => {
  const locationOptions = useMemo<SelectOption[]>(() => {
    const options: SelectOption[] = []

    if (!hideNoLocationOption) {
      const noLocationLabel = i18n.t('shift_management.add_shift.no_location')
      options.push({
        label: `${SORT_PRIORITY_PREFIX}${noLocationLabel}`,
        value: 0,
        disabled: false,
      })
    }

    if (mixedValuesLocation) {
      options.push({
        label: i18n.t('shift_management.edit_shifts.placeholder'),
        value: SYMBOLIC_MIXED_VALUE,
        disabled: false,
      })
    }

    const mappedLocations = locationsCollection.map((location) => ({
      label: location.attributes.name,
      value: location.attributes.id,
      disabled: false,
    }))
    options.push(...mappedLocations)

    return options
  }, [mixedValuesLocation, hideNoLocationOption])

  const workAreaOptions = useMemo<SelectOption[]>(() => {
    if (!locationField.value || locationField.value === SYMBOLIC_MIXED_VALUE) {
      return [
        {
          label: '',
          value: '',
          disabled: true,
        },
      ]
    }

    const noWorkAreaLabel = i18n.t('shift_management.add_shift.no_work_area')
    const options: SelectOption[] = [
      {
        label: `${SORT_PRIORITY_PREFIX}${noWorkAreaLabel}`,
        value: 0,
        disabled: false,
      },
    ]

    if (mixedValuesWorkArea && !locationField.isDirty) {
      options.push({
        label: i18n.t('shift_management.edit_shifts.placeholder'),
        value: SYMBOLIC_MIXED_VALUE,
        disabled: false,
      })
    }

    const mappedWorkAreas = workAreasCollection
      .filter({
        location_id: locationField.value,
        archived_at: null,
      })
      .map((workArea: WorkArea) => ({
        label: workArea.attributes.name,
        value: workArea.attributes.id,
        disabled: false,
      }))
    options.push(...mappedWorkAreas)

    return options
  }, [locationField.value, locationField.isDirty, mixedValuesWorkArea])

  return {
    locationOptions,
    workAreaOptions,
  }
}

export default useLocationOptions
